import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useForm} from '../../common/hooks/useForm'
import {useNotification} from '../../common/notifications/useNotification'
import {Product as ProductType, ProductRentalType} from '../../common/types/product'
import {AllProductsDocument, useEditProductMutation} from '../../generated/graphql'
import {transformProductRentalTypeToGQL} from '../../gql/transformProduct'
import {Box} from '../../webui/Box'
import {Button} from '../../webui/Button'
import {SaveButton} from '../../webui/buttons/SaveButton'
import {Drawer} from '../../webui/Drawer'
import {SelectInputProps} from '../../webui/inputs/Select'
import {Switch, SwitchProps} from '../../webui/inputs/Switch'
import {TextField, TextFieldProps} from '../../webui/inputs/TextField'
import {Typography} from '../../webui/Typography'
import {PriceWeightSelect} from '../price/PriceWeightSelect'
import {RentalTypeSelect} from './RentalTypeSelect'
import {ADD_PRODUCT_HELP as HELP} from './addProductHelpTexts'
import {
  NON_CANONICAL_PRODUCT_TRANSLATION_LANGUAGES,
  NonCanonicalProductTranslationLanguage,
  PRODUCT_TRANSLATION_LANGUAGE_LABELS,
} from './translationLanguages'

const HelpTip: React.FC<{text: string; id?: string; className?: string}> = ({text, id, className}) => {
  if (!text || !text.trim()) return null

  return (
    <span
      className={className}
      style={{
        position: 'relative',
        display: 'inline-block',
        marginLeft: 8,
      }}
    >
      <span
        id={id}
        aria-label="Help"
        role="img"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '1px solid currentColor',
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 600,
          cursor: 'default',
          userSelect: 'none',
        }}
      >
        ?
      </span>
      <span
        style={{
          position: 'absolute',
          zIndex: 10,
          top: '50%',
          left: '100%', // tooltip appears to the right of the icon
          right: 'auto',
          transform: 'translate(6px, -50%)',
          margin: 0,
          minWidth: 220,
          maxWidth: 360,
          background: 'rgba(0,0,0,0.85)',
          color: '#fff',
          padding: '8px 10px',
          borderRadius: 6,
          fontSize: 12,
          lineHeight: 1.4,
          boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 120ms ease, transform 120ms ease',
          whiteSpace: 'normal',
          textAlign: 'center',
        }}
        className="help-tip-tooltip"
      >
        {text}
      </span>
      <style>
        {`
          [aria-label="Help"]:hover + .help-tip-tooltip,
          [aria-label="Help"]:focus + .help-tip-tooltip,
          .help-tip-tooltip:hover {
            opacity: 1 !important;
            transform: translate(0, -50%) !important;
            pointer-events: auto !important;
          }
        `}
      </style>
    </span>
  )
}

const FieldLabel: React.FC<{label: string; help: string; top?: number}> = ({label, help, top = 0}) => (
  <Box mt={top} display="flex" alignItems="center">
    <Typography variant="subtitle2">{label}</Typography>
    <HelpTip text={help} />
  </Box>
)

interface EditProductDrawerProps {
  product: ProductType
  open: boolean
  onClose: () => void
}

interface TranslationFormEntry {
  name: string
  description: string
}

// Only the non-canonical languages live in the form's Translations section.
// English is taken from the top-level Name/Description fields on save.
type TranslationsForm = Record<NonCanonicalProductTranslationLanguage, TranslationFormEntry>

interface EditProductForm {
  description: string
  imageName: string
  maxRentTimeInMinutes?: number
  name: string
  priceWeight: string
  rentalType: ProductRentalType
  showInApi: boolean
  translationKey: string
  translations: TranslationsForm
}

// Read a per-language string from the i18n bundle (db-blob/catalog source). Returns undefined when
// the key has no value in that language. We deliberately bypass t()'s fallback chain here because we
// want to know whether *that specific language* has content, not what t() would resolve to.
const readBlobTranslation = (
  i18n: ReturnType<typeof useTranslation>['i18n'],
  lang: string,
  field: 'name' | 'description',
  translationKey: string | undefined,
): string => {
  if (!translationKey) return ''
  const value = i18n.getResource(lang, 'translation', `products.${field}.${translationKey}`)
  return typeof value === 'string' ? value : ''
}

const createInitialEditProductForm = (product: ProductType, i18n: ReturnType<typeof useTranslation>['i18n']): EditProductForm => {
  const rowHasData = !!product.translations
  const englishFromRow = product.translations?.en

  // Top-level Name/Description are the canonical English. Prefer the row's English translation when
  // present (Rule A: row claims the product); fall back to the original product columns.
  const name = englishFromRow?.name ?? product.name
  const description = englishFromRow?.description ?? product.description ?? ''

  // For each non-canonical language: when the row has any data, pre-populate from the row
  // (empty string for absent languages — Rule A says the blob is bypassed once the row claims the
  // product). When the row has no data, pre-populate from the i18n bundle so that saving will
  // promote the existing blob translations onto the row in one shot.
  const translations = NON_CANONICAL_PRODUCT_TRANSLATION_LANGUAGES.reduce<TranslationsForm>((acc, lang) => {
    if (rowHasData) {
      const entry = product.translations?.[lang]
      acc[lang] = {
        name: entry?.name ?? '',
        description: entry?.description ?? '',
      }
    } else {
      acc[lang] = {
        name: readBlobTranslation(i18n, lang, 'name', product.translationKey),
        description: readBlobTranslation(i18n, lang, 'description', product.translationKey),
      }
    }
    return acc
  }, {} as TranslationsForm)

  return {
    description,
    imageName: product.imageName ?? '',
    maxRentTimeInMinutes: product.maxRentTimeInMinutes,
    name,
    priceWeight: product.priceWeight.id,
    rentalType: product.rentalType,
    showInApi: product.showInApi,
    translationKey: product.translationKey ?? '',
    translations,
  }
}

// Build the GraphQL translations input. English is always present (from the top-level fields).
// Non-canonical languages are included only when the form has any non-empty content for them.
// Once any non-canonical language is included, that language's name/description are written
// (with empty strings becoming nulls so partially-filled languages still write back cleanly).
const buildTranslationsInput = (englishName: string, englishDescription: string, translations: TranslationsForm) => {
  const result: Record<string, {name: string | null; description: string | null}> = {
    en: {name: englishName, description: englishDescription},
  }
  for (const lang of NON_CANONICAL_PRODUCT_TRANSLATION_LANGUAGES) {
    const entry = translations[lang]
    const name = entry.name.trim()
    const description = entry.description.trim()
    if (name || description) {
      result[lang] = {
        name: name || null,
        description: description || null,
      }
    }
  }
  return result
}

export const EditProductDrawer: React.FC<EditProductDrawerProps> = ({product, open, onClose}) => {
  const {i18n} = useTranslation()

  const initialForm = useMemo(() => createInitialEditProductForm(product, i18n), [product, i18n])
  const {form, updateField, reset} = useForm(initialForm)

  const [editProduct, {loading}] = useEditProductMutation()
  const {showNotification} = useNotification()

  const onNameChange: TextFieldProps['onChange'] = (value) => updateField('name', value)
  const onImageNameChange: TextFieldProps['onChange'] = (value) => updateField('imageName', value)
  const onDescriptionChange: TextFieldProps['onChange'] = (value) => updateField('description', value)
  const onPriceWeightChange: SelectInputProps<string>['onChange'] = (e) => updateField('priceWeight', e.target.value as string)
  const onRentalTypeChange = (value: ProductRentalType) => updateField('rentalType', value)
  const onShowOnApiChange: SwitchProps['onChange'] = (value) => updateField('showInApi', value)
  const onTranslationKeyChange: TextFieldProps['onChange'] = (value) => updateField('translationKey', value)
  const onMaxRentTimeInMinutesChange: TextFieldProps['onChange'] = (value) => {
    const valueNumber = parseInt(value, 10)
    updateField('maxRentTimeInMinutes', isNaN(valueNumber) ? undefined : valueNumber)
  }

  const onTranslationFieldChange =
    (lang: NonCanonicalProductTranslationLanguage, field: keyof TranslationFormEntry): TextFieldProps['onChange'] =>
    (value) => {
      updateField('translations', {
        ...form.translations,
        [lang]: {
          ...form.translations[lang],
          [field]: value,
        },
      })
    }

  const onCancel = () => {
    reset()
    onClose()
  }

  const onSave = async () => {
    const englishName = form.name.trim()
    const englishDescription = form.description.trim()
    if (!englishName || !englishDescription) {
      showNotification({
        message: 'Name and Description are required.',
        severity: 'error',
      })
      return
    }

    try {
      const result = await editProduct({
        variables: {
          productId: product.id,
          editProduct: {
            description: form.description,
            imageName: form.imageName,
            maxRentTimeInMinutes: form.maxRentTimeInMinutes,
            name: form.name,
            priceWeight: form.priceWeight || undefined,
            rentalType: transformProductRentalTypeToGQL(form.rentalType),
            showInApi: form.showInApi,
            translationKey: form.translationKey,
            translations: buildTranslationsInput(englishName, englishDescription, form.translations),
          },
        },
        refetchQueries: [{query: AllProductsDocument}],
      })
      showNotification({message: 'Product edited.', severity: 'success'})
      console.log('Product edited!', result?.data?.editProduct?.product)
      onClose()
    } catch (e) {
      showNotification({
        message: 'Could not edit product.',
        severity: 'error',
      })
      console.log('Product edit error', e, '--- e')
    }
  }

  return (
    <Drawer anchor="right" onClose={onClose} open={open} PaperProps={{style: {width: 600}}}>
      <Box m={2}>
        <Typography variant="h4">Edit product "{product.name}"</Typography>
        <form noValidate autoComplete="off">
          <Box mt={2} display="flex" flexDirection="column">
            <FieldLabel label="Name (English)" help={HELP.name} />
            <TextField id="edit-product-name" value={form.name} onChange={onNameChange} />

            <Box mt={2} />
            <FieldLabel label="Description (English)" help={HELP.description} />
            <TextField id="edit-product-description" multiline value={form.description} onChange={onDescriptionChange} />

            <Box mt={3}>
              <Typography variant="subtitle2" fontWeight={600}>
                Translations
              </Typography>
              <Typography variant="body2" sx={{mb: 1, opacity: 0.8}}>
                Translations for additional languages. Optional — missing languages fall back to English.
              </Typography>
              {NON_CANONICAL_PRODUCT_TRANSLATION_LANGUAGES.map((lang) => {
                const entry = form.translations[lang]
                const isFilled = !!(entry.name.trim() || entry.description.trim())
                return (
                  <Box key={lang} mt={1.5} p={1.5} border="1px solid rgba(0,0,0,0.08)" borderRadius={4}>
                    <Box display="flex" alignItems="center">
                      <img src={`/flags/flag-${lang}.svg`} alt={`flag-${lang}`} width={20} height={20} />
                      <Box ml={1}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {PRODUCT_TRANSLATION_LANGUAGE_LABELS[lang]}
                        </Typography>
                      </Box>
                      <Box ml={1}>
                        <Typography variant="body2" sx={{opacity: 0.6}}>
                          {isFilled ? '' : '(empty — will fall back to English)'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box mt={0.5}>
                      <Typography variant="body2">Name</Typography>
                      <TextField id={`edit-product-translation-${lang}-name`} value={entry.name} onChange={onTranslationFieldChange(lang, 'name')} />
                    </Box>
                    <Box mt={1}>
                      <Typography variant="body2">Description</Typography>
                      <TextField
                        id={`edit-product-translation-${lang}-description`}
                        multiline
                        value={entry.description}
                        onChange={onTranslationFieldChange(lang, 'description')}
                      />
                    </Box>
                  </Box>
                )
              })}
            </Box>

            <Box mt={2} />
            <FieldLabel label="Image name" help={HELP.imageName} />
            <TextField id="edit-product-imageName" multiline value={form.imageName} onChange={onImageNameChange} />

            <Box mt={2} />
            <FieldLabel label="Translation key" help={HELP.translationKey} />
            <TextField id="edit-product-translationKey" multiline value={form.translationKey} onChange={onTranslationKeyChange} />

            <Box mt={2} />
            <FieldLabel label="Max rent time in minutes" help={HELP.maxRentTimeInMinutes} />
            <TextField id="edit-product-maxRentTime" value={form.maxRentTimeInMinutes} onChange={onMaxRentTimeInMinutesChange} />

            <Box mt={2} />
            <FieldLabel label="Price weight" help={HELP.priceWeight} />
            <PriceWeightSelect id="edit-product-priceWeight" onChange={onPriceWeightChange} value={form.priceWeight} />

            <Box mt={2} />
            <FieldLabel label="Rental type" help={HELP.rentalType} />
            <RentalTypeSelect id="edit-product-rentalType" value={form.rentalType} onChange={onRentalTypeChange} />

            <Box mt={2} />
            <Box display="flex" alignItems="center">
              <FieldLabel label="Show in API" help={HELP.showInApi} />
              <Box ml={2} />
              <Switch onChange={onShowOnApiChange} value={form.showInApi} label={undefined as any} />
            </Box>
          </Box>
        </form>
        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Button onClick={onCancel}>Cancel</Button>
          <Box ml={2} />
          <SaveButton loading={loading} onClick={onSave} />
        </Box>
      </Box>
    </Drawer>
  )
}
